import { useEffect, useState } from "react";
import axios from "axios";
import { Boxes, Linkedin, Youtube, ExternalLink, ArrowRight, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = "primes4.brand.logo.v1";

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.logo === "string") return parsed;
  } catch { /* ignore */ }
  return null;
};

const writeCache = (logo) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ logo, fetched_at: Date.now() }));
  } catch { /* ignore */ }
};

export const Footer = ({ hideSapBadge = false }) => {
  const cached = readCache();
  const [logo, setLogo] = useState(cached ? cached.logo : "");
  const [resolved, setResolved] = useState(cached !== null);

  // Mirror the Header logo lookup: pull the admin-configured branding logo so
  // the footer mark updates automatically when an admin changes branding.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/branding/public`)
      .then((r) => {
        if (cancelled) return;
        const b = r.data || {};
        const next = b.platform_logo || "";
        setLogo(next);
        setResolved(true);
        writeCache(next);
      })
      .catch(() => { if (!cancelled) setResolved(true); });
    return () => { cancelled = true; };
  }, []);

  const openRequestDemo = () => {
    window.dispatchEvent(new CustomEvent("requestDemo:open"));
  };

  return (
    <footer
      data-testid="app-footer"
      className="relative text-white"
      style={{
        background:
          "linear-gradient(180deg, #062B3D 0%, #04202F 60%, #03182A 100%)",
      }}
    >
      {/* Subtle accent line at the top */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(94,184,216,0.45) 50%, transparent 100%)",
        }}
      />
      {/* Soft ambient glows */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-24 left-[10%] w-[420px] h-[420px] rounded-full blur-3xl opacity-[0.12]"
          style={{ background: "radial-gradient(circle, #0093cb 0%, transparent 65%)" }}
        />
        <div
          className="absolute -bottom-32 right-[10%] w-[360px] h-[360px] rounded-full blur-3xl opacity-[0.10]"
          style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 65%)" }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* LEFT — brand + tagline + address */}
          <div className="lg:col-span-5" data-testid="footer-brand">
            <a
              href="/"
              className="inline-flex items-center gap-2.5"
              data-testid="footer-logo-link"
            >
              {logo ? (
                <img loading="lazy"
                  src={logo}
                  alt="PrimeS4 AI"
                  className="h-8 w-auto object-contain"
                  data-testid="footer-brand-logo"
                />
              ) : resolved ? (
                <>
                  <div className="w-8 h-8 rounded-sm flex items-center justify-center bg-white/10 border border-white/15">
                    <Boxes className="w-4.5 h-4.5 text-[#7DD3FC]" strokeWidth={1.5} />
                  </div>
                  <span
                    className="font-semibold text-lg tracking-tight text-white"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                  >
                    PrimeS4 AI
                  </span>
                </>
              ) : (
                <div aria-hidden="true" className="h-8 w-32" />
              )}
            </a>

            <p
              className="mt-5 text-[13.5px] leading-relaxed text-white/70 max-w-md"
              data-testid="footer-tagline"
            >
              PRIVA-powered, SAP-aligned operational intelligence for enterprise transformation and AI-assisted execution.
            </p>

            <div className="mt-6 text-[12.5px] leading-relaxed text-white/55" data-testid="footer-address">
              <p className="text-white/80 font-semibold tracking-wide">
                PrimeS4 AI Technologies LLC-FZ
              </p>
              <p>Dubai, United Arab Emirates</p>
            </div>
          </div>

          {/* CENTER — primary CTA */}
          <div
            className="lg:col-span-3 flex lg:items-center lg:justify-center"
            data-testid="footer-cta"
          >
            <button
              type="button"
              onClick={openRequestDemo}
              data-testid="footer-cta-request-demo"
              className="group inline-flex items-center justify-center gap-2 rounded-md h-12 px-7 text-sm font-semibold tracking-wide text-white transition-all duration-200 shadow-[0_10px_30px_-10px_rgba(0,147,203,0.55)] hover:shadow-[0_14px_36px_-10px_rgba(0,147,203,0.7)] hover:-translate-y-0.5"
              style={{ backgroundColor: "#0093cb" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#03738C")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0093cb")}
            >
              Request Demo
              <ArrowRight
                className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2}
              />
            </button>
          </div>

          {/* RIGHT — group + social */}
          <div className="lg:col-span-4 lg:text-right" data-testid="footer-group">
            <p className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-[#7DD3FC]">
              Part of PrimeS4 Group
            </p>
            <a
              href="https://primes4.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-primes4-link"
              className="group mt-3 inline-flex items-center gap-1.5 text-[14px] font-medium text-white hover:text-[#7DD3FC] transition-colors"
            >
              Visit PrimeS4.com
              <ExternalLink
                className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity"
                strokeWidth={1.8}
              />
            </a>
            <div className="mt-5 flex items-center gap-2.5 lg:justify-end">
              <a
                href="https://www.linkedin.com/company/primes4-technologies-llc/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PrimeS4 on LinkedIn"
                data-testid="footer-linkedin"
                className="inline-flex items-center justify-center w-10 h-10 rounded-sm border border-white/15 bg-white/[0.04] text-white/75 hover:text-white hover:bg-white/[0.08] hover:border-white/30 transition-colors"
              >
                <Linkedin className="w-4 h-4" strokeWidth={1.8} />
              </a>
              <a
                href="https://www.youtube.com/@PrimeS4"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PrimeS4 on YouTube"
                data-testid="footer-youtube"
                className="inline-flex items-center justify-center w-10 h-10 rounded-sm border border-white/15 bg-white/[0.04] text-white/75 hover:text-white hover:bg-white/[0.08] hover:border-white/30 transition-colors"
              >
                <Youtube className="w-4 h-4" strokeWidth={1.8} />
              </a>
            </div>

            {/* SAP Silver Partner — quiet credibility mark under the group block */}
            {!hideSapBadge && (
              <div
                className="mt-6 flex lg:justify-end"
                data-testid="footer-sap-silver-partner-wrap"
              >
                <div
                  aria-label="SAP Silver Partner"
                  data-testid="footer-sap-silver-partner"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-white/[0.04] border border-white/10"
                >
                  <img
                    src="/images/image-14.webp"
                    alt="SAP Silver Partner"
                    className="h-9 sm:h-10 w-auto object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-white/10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[11.5px] text-white/55" data-testid="footer-copyright">
              © {new Date().getFullYear()} PrimeS4 AI. All Rights Reserved.
            </p>
            <p
              className="text-[11.5px] font-medium tracking-wide text-white/65"
              data-testid="footer-disciplines"
            >
              SAP S/4HANA <span className="text-white/30 mx-1.5">•</span> SCM{" "}
              <span className="text-white/30 mx-1.5">•</span> AI Transformation
            </p>
          </div>
          <p
            className="mt-3 flex items-center gap-1.5 text-[10.5px] tracking-[0.14em] uppercase text-white/40 sm:justify-center"
            data-testid="footer-powered-by"
          >
            <Sparkles className="w-3 h-3 text-[#A78BFA]" strokeWidth={1.8} />
            Powered by PRIVA Intelligence Layer
          </p>
        </div>
      </div>
    </footer>
  );
};
